/**
 * Manages the axios instances creation.
 *
 * @author Hugo Azevedo
 */
import axios from 'axios';
import axiosRetry from 'axios-retry';
import { withDefaultInterceptors } from './interceptors';

function addRetryableScope(axiosInstance, options) {
  const retries = options.maxRetries || 3;
  const retryDelay = options.retryDelay || axiosRetry.exponentialDelay;
  const retryCondition = options.retryCondition || axiosRetry.isNetworkOrIdempotentRequestError;
  axiosRetry(axiosInstance, {
    retries,
    retryDelay,
    retryCondition,
  });
}

/**
 * @module createAxiosIntance
 * @example
 * const baseURL = 'http://stilingue.com.br';
 * const timeout = 10000; // 10 seconds
 * const useDefaultInterceptors = true;
 * const http = StRequests.createAxiosIntance({baseURL, timeout}, useDefaultInterceptors)
 *
 * // Now, this custom http requester can be used to instantiate a new api.
 * const api = new StRequests('', http);
 * api.get() // Will make a get request to 'http://stilingue.com.br' with timeout of 10 seconds.
 * @description Creates a new axios instance with the given options.
 * @param {Object} options Instance options
 * @param {boolean} [useDefaultInterceptors=true] Indicates wheter to use the default
 * interceptors or not.
 * @returns {axios} The created axios instance
 */
export default function createAxiosIntance(options, useDefaultInterceptors = true) {
  const baseURL = options.baseURL || '';
  const timeout = options.timeout || 30000;

  const axiosInstance = axios.create({
    ...options,
    baseURL,
    timeout,
  });

  if (options.retry) {
    addRetryableScope(axiosInstance, options);
  }

  return useDefaultInterceptors ? withDefaultInterceptors(axiosInstance, options) : axiosInstance;
}
